import * as XLSX from 'xlsx';

// CSV Exporter and Data Mapper for MIS and Summary formats

export const generateTransactionCode = (item, monthCounters = {}) => {
  if (item?.remarks && typeof item.remarks === 'string' && /^[A-Z]{3}\d{3,};$/.test(item.remarks.trim())) {
    return item.remarks.trim();
  }

  const rawDate = item?.Date || item?.date || item?.legDate || item?.tripDate || item?.trip_date || item?.tripdate || item?.createdAt || item?.created_at;
  let dateObj = rawDate ? new Date(rawDate) : null;
  if (!dateObj || isNaN(dateObj.getTime())) {
    dateObj = new Date();
  }

  const monthStr = dateObj.toLocaleString('en-US', { month: 'short' }).toUpperCase();
  monthCounters[monthStr] = (monthCounters[monthStr] || 0) + 1;
  const countStr = String(monthCounters[monthStr]).padStart(3, '0');

  return `${monthStr}${countStr};`;
};

export const formatDelayPenalty = (val) => {
  if (val === null || val === undefined || val === '') return 'Null';
  const strVal = String(val).trim();
  if (strVal.toLowerCase() === 'null' || strVal === '-' || strVal === '0' || strVal === '0.00') {
    return 'Null';
  }
  const num = parseFloat(strVal);
  if (!isNaN(num) && num > 0) {
    return Number(num.toFixed(2));
  }
  return 'Null';
};

export const mapSSTDetailsData = (sstData) => {
  const monthCounters = {};
  return (sstData || [])
    .map((item, index) => {
      const rawDelayPenalty =
        item.delayPenalties !== undefined && item.delayPenalties !== null ? item.delayPenalties :
        item.delay_penalties !== undefined && item.delay_penalties !== null ? item.delay_penalties :
        item.delayPenalty !== undefined && item.delayPenalty !== null ? item.delayPenalty :
        item.delay_penalty !== undefined && item.delay_penalty !== null ? item.delay_penalty :
        item.penalty !== undefined && item.penalty !== null ? item.penalty :
        item.penalties !== undefined && item.penalties !== null ? item.penalties :
        null;

      return {
        slNo: item.slNo || index + 1,
        clientName: item.clientName || '',
        date: item.Date || item.date || item.legDate || item.tripDate || item.trip_date || item.tripdate || item.createdAt || item.created_at || '',
        tripNo: item.tripNo || item.tripsNo || '',
        vehNo: item.vehNo || item.vehicleNo || item.vehicleNumber || '',
        pickupDrop: item.pickupDrop || item.tripType || '',
        time: item.time || item.pickupTime || '',
        vehicleType: item.vehicleType || '',
        rosteredEmp: item.rosteredEmp || item.rosteredEmployee || '',
        boardedEmp: item.boardedEmp || item.boardedEmployee || '',
        location: item.location || item.zone || '',
        escort: item.escort !== null && item.escort !== undefined ? (item.escort ? 'Yes' : 'No') : '',
        kms: item.kms || item.km || '',
        slab: item.slab || '',
        tripAmount: item.tripAmount || '',
        escortAmount: item.escortAmount || '',
        totalAmount: item.totalAmount || '',
        delayPenalties: formatDelayPenalty(rawDelayPenalty),
        remarks: generateTransactionCode(item, monthCounters)
      };
    })
    .filter(row => row.slNo || row.clientName || row.date || row.tripNo || row.vehNo);
};

export const generateMISRows = (detailsData) => {
  const headers = [
    'Sl No', 'Client Name', 'Date', 'Trip No', 'Veh No', 'Pickup/Drop', 'Time',
    'Vehicle Type', 'Rostered Emp', 'Boarded Emp', 'Location', 'Escort', 'KMs',
    'Slab', 'Trip Amount', 'Escort Amount', 'Total Amount', 'Delay Penalties', 'Remarks'
  ];

  const rows = (detailsData || []).map(row => [
    row.slNo, row.clientName, row.date, row.tripNo, row.vehNo, row.pickupDrop,
    row.time, row.vehicleType, row.rosteredEmp, row.boardedEmp, row.location,
    row.escort, row.kms, row.slab, row.tripAmount, row.escortAmount,
    row.totalAmount, (row.delayPenalties !== null && row.delayPenalties !== undefined && row.delayPenalties !== '') ? row.delayPenalties : 'Null', row.remarks
  ]);

  return [headers, ...rows];
};

export const generateMISCSVContent = (detailsData) => {
  const tableData = generateMISRows(detailsData);
  return tableData.map(row => row.map(val => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }).join(',')).join('\n');
};

export const generateSummaryRows = (detailsData, sheetDate) => {
  // Group trips by vehicle number
  const vehicleMap = {};

  (detailsData || []).forEach(item => {
    const veh = item.vehNo ? String(item.vehNo).trim().toUpperCase() : 'UNKNOWN';
    if (!vehicleMap[veh]) {
      vehicleMap[veh] = {
        vehNo: veh,
        tripsCount: 0,
        totalAmount: 0,
        delayPenalties: 0
      };
    }
    const amt = parseFloat(item.totalAmount || item.tripAmount || 0) || 0;
    vehicleMap[veh].tripsCount += 1;
    vehicleMap[veh].totalAmount += amt;

    const penaltyVal = (item.delayPenalties !== 'Null' && item.delayPenalties !== null && item.delayPenalties !== undefined)
      ? parseFloat(item.delayPenalties) || 0
      : 0;
    vehicleMap[veh].delayPenalties += penaltyVal;
  });

  const headers = [
    'Cab Num', 'Tot Trips', 'Amount', 'GPS Rent', 'Diesel', 'Diesel Int',
    'Office Charges', 'Delay Penalties', 'Payment', 'TDS', 'Total Amount', 'Remarks'
  ];

  const rows = Object.values(vehicleMap).map(v => {
    const totTrips = v.tripsCount;
    const amount = Number(v.totalAmount.toFixed(2));
    const gpsRent = totTrips > 0 ? 118 : 0;
    const diesel = 0;
    const dieselInt = 0;
    const officeCharges = Number((amount * 0.02).toFixed(2));
    const delayPenaltyVal = v.delayPenalties > 0 ? Number(v.delayPenalties.toFixed(2)) : 0;
    const delayPenaltyDisplay = v.delayPenalties > 0 ? delayPenaltyVal : 'Null';
    const payment = Number((amount - gpsRent - diesel - dieselInt - officeCharges - delayPenaltyVal).toFixed(2));
    const tds = Number((payment * 0.01).toFixed(2));
    const finalTotal = Number((payment - tds).toFixed(2));
    const remarks = sheetDate || 'Monthly Summary';

    return [
      v.vehNo,
      totTrips,
      amount,
      gpsRent,
      diesel,
      dieselInt,
      officeCharges,
      delayPenaltyDisplay,
      payment,
      tds,
      finalTotal,
      remarks
    ];
  });

  return [headers, ...rows];
};

export const generateSummaryCSVContent = (detailsData, sheetDate) => {
  const tableData = generateSummaryRows(detailsData, sheetDate);
  return tableData.map(row => row.map(val => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }).join(',')).join('\n');
};

export const triggerExcelDownload = (tableData, fileName, sheetName = 'Sheet1') => {
  const ws = XLSX.utils.aoa_to_sheet(tableData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
};

export const triggerCSVDownload = (csvContent, fileName) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
